import {
  downloadToCSV,
  TimeLineTable,
  useTimer,
  useTimeZone,
  useTranslation,
  zonedToUTC,
  zonedToUTCAdd1Day
} from '@fareye/common'
import { LANGUAGE_STRINGS } from '@fareye/configs'
import { AllIssueListing } from '@fareye/issue-resolution'
import { Flex, RenderControl } from '@fareye/ui'
import {
  camelCase,
  cancelToken,
  currenthhmmA,
  dMmm,
  getQueryParams,
  getXlsx,
  postXlsx,
  groupBy,
  HEADERS,
  isEmpty,
  post,
  snakeCase,
  serialzeAggregationsToSummaryData
} from '@fareye/utils'
import queryString from 'query-string'
import React, { useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { mapping } from '../../../config/mapComponents'
import { getTableColumns } from '../components/Column'

const initApiResponse = {
  data: [],
  isLoading: false,
  isError: false,
  isSuccess: true,
  message: '',
  meta: {
    count: -1,
    pageNumber: 1
  }
}

const ContainerTable = React.memo((props: any) => {
  let {
    match,
    timer,
    config,
    baseUrl,
    endDate,
    location,
    startDate,
    showDatePicker = true,
    refreshableTable = true,
    globalSearch,
    showTimezone
  } = props
  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0
  })
  const [apiData, setApiResponse] = React.useState<any>(initApiResponse)
  const [sorting, setSortBy] = React.useState({
    sortBy: '',
    sortByDirection: true
  })
  const { t } = useTranslation()
  const source = cancelToken().source()
  const [changeDetect, setChangeDetect] = React.useState(false)
  const [pageNo, setPageNo] = React.useState(0)
  const [recordsPerPage, setRecordsPerPage] = React.useState(25)
  const query = queryString.parse(location.search)
  const { style, className, variant } = config.styling
  const { state } = useTimeZone()
  const { links, apiConfig, sortingMapping } = config.config
  const { sortBy, sortByDirection } = sorting
  const searchParams = {
    ...query,
    // startDate,
    // endDate,
    pageNo: pageNo + 1,
    recordsPerPage
  }
  const subComponentConfig = config.children
  const tableLinks: any = { ...links }
  const selectedColumn = getTableColumns(config.config.columnsList, {
    t,
    links: config.config.links,
    queryParams: {}
  })
    .filter((value: any, index: number, arr: any) => {
      // removes duplicate column with same id
      return arr.findIndex((element: any) => element.id == value.id) === index
    })
    .map((item: any) => ({
      ...item,
      sortable: (apiData?.meta?.sortableAttributes || []).includes(
        item.id
          .split('.')
          .map((item: string) => camelCase(item))
          .join('.')
      )
    }))
  const isFirstRun = useRef(false)
  React.useEffect(() => {
    if (isFirstRun.current) {
      setPageNo(0)
      setApiResponse(initApiResponse)
      setChangeDetect(!changeDetect)
    }
    isFirstRun.current = true
  }, [startDate, endDate, globalSearch, recordsPerPage])

  React.useEffect(() => {
    fetchData()
  }, [pageNo, changeDetect, sorting, timer])

  function generateFilterDataFromGlobalSearch(globalSearch: any[]) {
    if (isEmpty(globalSearch)) {
      return []
    } else {
      const groupedSearch = groupBy(globalSearch, ele => ele.key)
      return Object.entries(groupedSearch).map(([key, value]) => {
        return [
          {
            filterKey: key,
            filterValue: value.reduce((res = [], obj) => {
              res.push(obj.value)
              return res
            }, []),
            condition: 'include',
            filterType: 'eq'
          }
        ]
      })
    }
  }
  function generateFilterObjectForDateRange() {
    const startFilter = [
      {
        filterKey: 'created_at',
        filterValue: startDate ? zonedToUTC(startDate) : new Date(),
        condition: 'include',
        filterType: 'gte'
      }
    ]
    const endFilter = [
      {
        filterKey: 'created_at',
        filterValue: endDate ? zonedToUTCAdd1Day(endDate) : new Date(),
        condition: 'include',
        filterType: 'lt'
      }
    ]
    const { queryParams } = apiConfig
    const statusFilter = !isEmpty(queryParams)
      ? Object.keys(queryParams).map((item: string) => queryParams[item])
      : []
    if (showDatePicker && !isEmpty(statusFilter)) {
      return [startFilter, endFilter, statusFilter]
    } else if (!showDatePicker && !isEmpty(statusFilter)) {
      return [statusFilter]
    } else if (showDatePicker && isEmpty(statusFilter)) {
      return [startFilter, endFilter]
    } else {
      ;[]
    }
  }

  function generateSortingObj() {
    const { sortByDirection, sortBy } = sorting
    if (sortBy) {
      const snakeParams = sortBy
        .split('.')
        .map(i => snakeCase(i))
        .join('.')
      const sort = [
        { sortBy: snakeParams, sortDir: sortByDirection ? 'asc' : 'desc' }
      ]
      return { sort }
    } else {
      return { sort: [{ sortBy: 'created_at', sortDir: 'desc' }] }
    }
  }

  async function fetchData() {
    try {
      setApiResponse({
        ...apiData,
        isLoading: true
      })
      let filter = generateFilterDataFromGlobalSearch(globalSearch)
      const dateFilter: any = generateFilterObjectForDateRange()
      filter = [...filter, ...dateFilter]
      const { apiURL } = apiConfig
      const {
        data: { results, meta }
      } = await post(
        apiURL,
        {
          filter,
          perPage: recordsPerPage,
          page: pageNo + 1,
          ...generateSortingObj()
        },
        source.token,
        {
          headers: HEADERS()['HEADER_INFLECTION']
        }
      )
      // merge the data with previous state on the page index
      // of which data is fetched of page size assuming pageindex starts from 0
      // ex pageIndex = 3 pageSize = 10 t.splice(30, 10, ...data)
      // this implemetation covers empty array refresh of same pageIndex
      // const tempData = results
      const tempData = [...apiData.data]
      tempData.splice(pageNo * recordsPerPage, recordsPerPage, ...results)
      // tempData.splice(pageNo * recordsPerPage, recordsPerPage, ...results)
      if (results) {
        setApiResponse({
          ...apiData,
          data: tempData,
          isSuccess: true,
          meta: {
            ...meta,
            sortableAttributes: meta.sortableAttributes.map((column: string) =>
              column
                .split('.')
                .map((item: string) => camelCase(item))
                .join('.')
            )
          },
          isError: false,
          message: isEmpty(results) ? 'No Data Found...' : '',
          isLoading: false
        })
      }
    } catch (ex:any) {
      setApiResponse({
        data: [],
        isSuccess: false,
        isError: true,
        message: t('Something went wrong...'),
        isLoading: false
      })
    }
  }
  return (
    <RenderControl state={apiData} variant={'xl'} t={t}>
      <Flex column>
        <div style={style}>
          <TimeLineTable
            verticalAlign={true}
            className={`responsive striped ${className} multi-model`}
            data={apiData.data}
            padding="4px 10px"
            size="s"
            showTotalCount={false}
            sorting={sorting}
            sortable={true}
            totalElements={apiData?.meta?.count || apiData.data.length}
            columns={selectedColumn}
            onSortedChange={(ev: any) => {
              setSortBy({
                sortBy: ev[0].id,
                sortByDirection:
                  ev[0].id === sorting.sortBy
                    ? !sorting.sortByDirection
                    : !ev[0].desc
              })
            }}
            defaultPageSize={recordsPerPage}
            onPageSizeChange={(pageSize: number) => {
              setPageNo(0)
              setRecordsPerPage(pageSize)
            }}
            defaultPageIndex={pageNo}
            onPageChange={(pageIndex: number) => {
              setPageNo(pageIndex)
            }}
          />
        </div>
        <div ref={ref} />
      </Flex>
    </RenderControl>
  )
})

export const ContainerStatusTable = (props: any) => {
  const { config, maxRefreshCount, refreshInterval } = props
  const { minAutoRefreshInterval } = config?.config || {}
  const [timer] = useTimer({
    maxRefreshCount,
    refreshInterval,
    minAutoRefreshInterval
  })
  return <ContainerTable {...props} timer={timer} />
}
